import { useState, useEffect, useRef } from 'react'
import { supabase } from './supabase'
import NewBundle from './NewBundle'

function renderMessageContent(text, allBundles, addedBundles, onToggleBundle, fetchingBundle, builtBundles, onBuildBundle) {
  const parts = text.split(/\[\[(.+?)\]\]/gs)
  return parts.map((part, i) => {
    if (i % 2 === 1) {

      // Live recipe card from Brave Search
      if (part.startsWith('RECIPE:')) {
        const content = part.slice(7)
        const parts3 = content.split('|||')
        const title = parts3[0] || ''
        const url = parts3[1] || ''
        const site = parts3[2] || ''
        const isBuilding = fetchingBundle === url
        const isBuilt = builtBundles.has(url)
        return (
          <div key={i} style={{
            border: `1px solid ${isBuilt ? '#1D9E75' : '#e8e8e8'}`,
            borderRadius: '14px',
            padding: '12px 14px',
            margin: '6px 0',
            background: isBuilt ? '#f4fbf8' : 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a18', lineHeight: '1.4' }}>
              {title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '11px', color: '#888', textDecoration: 'none', flexShrink: 0 }}
              >
                {site} ↗
              </a>
              <button
                style={{
                  padding: '7px 14px', borderRadius: '20px', border: 'none',
                  background: isBuilt ? '#E1F5EE' : isBuilding ? '#ccc' : '#1D9E75',
                  color: isBuilt ? '#0F6E56' : 'white',
                  fontSize: '12px', fontWeight: '600',
                  cursor: isBuilding || isBuilt ? 'default' : 'pointer',
                  fontFamily: "'DM Sans', sans-serif", flexShrink: 0,
                  letterSpacing: '0.01em',
                }}
                onClick={() => !isBuilding && !isBuilt && onBuildBundle(title, url)}
                disabled={isBuilding || isBuilt}
              >
                {isBuilding ? 'Building…' : isBuilt ? '✓ Saved' : '+ Build bundle'}
              </button>
            </div>
          </div>
        )
      }

      // New recipe pill (fallback when no Brave results)
      if (part.startsWith('NEW:')) {
        const recipeName = part.slice(4).trim()
        const isBuilding = fetchingBundle === recipeName
        const isBuilt = builtBundles.has(recipeName)
        return (
          <span
            key={i}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              background: isBuilt ? '#E1F5EE' : '#f4f0ff',
              color: isBuilt ? '#0F6E56' : '#6633cc',
              borderRadius: '20px', padding: '3px 10px 3px 8px',
              fontSize: '13px', fontWeight: '500',
              cursor: isBuilding || isBuilt ? 'default' : 'pointer',
              margin: '0 2px', border: `1px solid ${isBuilt ? '#1D9E75' : '#c4a8ff'}`,
            }}
            onClick={() => !isBuilding && !isBuilt && onBuildBundle(recipeName, null)}
          >
            <span>{isBuilt ? '✓' : '✨'}</span>
            <span>{recipeName}</span>
            <span style={{ fontSize: '12px', opacity: 0.8 }}>
              {isBuilding ? 'Building…' : isBuilt ? 'Saved' : 'Build bundle'}
            </span>
          </span>
        )
      }

      // Existing saved bundle pill
      const bundle = allBundles.find(b => b.name.toLowerCase() === part.toLowerCase())
      if (bundle) {
        const added = addedBundles.has(bundle.id)
        return (
          <span
            key={i}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              background: added ? '#E1F5EE' : '#1D9E75',
              color: added ? '#0F6E56' : 'white',
              borderRadius: '20px', padding: '3px 10px 3px 7px',
              fontSize: '13px', fontWeight: '500',
              cursor: 'pointer', margin: '0 2px',
              border: added ? '1px solid #1D9E75' : 'none',
            }}
            onClick={() => onToggleBundle(bundle)}
          >
            <span>{bundle.emoji}</span>
            <span>{bundle.name}</span>
            <span style={{ fontSize: '13px', marginLeft: '2px' }}>{added ? '✓' : '+'}</span>
          </span>
        )
      }
      return part
    }
    return part
  })
}


export default function Assistant({ user, onBack, customTabs = [] }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [initializing, setInitializing] = useState(true)
  const [bundles, setBundles] = useState([])
  const [cartBundles, setCartBundles] = useState([])
  const [cartItems, setCartItems] = useState([])
  const [planHistory, setPlanHistory] = useState([])
  const [householdId, setHouseholdId] = useState(null)
  const [addedBundles, setAddedBundles] = useState(new Set())
  const [fetchingBundle, setFetchingBundle] = useState(null)
  const [pendingBundle, setPendingBundle] = useState(null)
  const [builtBundles, setBuiltBundles] = useState(new Set())
  const bottomRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => { fetchContext() }, [])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  async function fetchContext() {
    const { data: membership } = await supabase
      .from('household_members').select('household_id')
      .eq('user_id', user.id).maybeSingle()
    if (!membership) { setInitializing(false); return }

    const hid = membership.household_id
    setHouseholdId(hid)

    const [
      { data: bundleRows },
      { data: cartBundleRows },
      { data: cartItemRows },
      { data: planRows },
    ] = await Promise.all([
      supabase.from('bundles').select('id, name, emoji, category').eq('household_id', hid).order('sort_order', { ascending: true }),
      supabase.from('bundles').select('id, name, emoji').eq('household_id', hid).eq('in_cart', true),
      supabase.from('cart_items').select('name, emoji').eq('household_id', hid),
      supabase.from('meal_plan_entries')
        .select('week_start, bundles(id, name, emoji)')
        .eq('household_id', hid)
        .order('week_start', { ascending: false })
        .limit(60),
    ])

    setBundles(bundleRows || [])
    setCartBundles(cartBundleRows || [])
    setCartItems(cartItemRows || [])

    const grouped = {}
    ;(planRows || []).forEach(e => {
      if (!grouped[e.week_start]) grouped[e.week_start] = []
      grouped[e.week_start].push(e)
    })
    const history = Object.entries(grouped).slice(0, 8).map(([week, entries]) => ({ week, entries }))
    setPlanHistory(history)

    setAddedBundles(new Set((cartBundleRows || []).map(b => b.id)))
    setInitializing(false)
  }

  async function buildBundle(recipeName, url = null) {
    const key = url || recipeName
    if (fetchingBundle || builtBundles.has(key)) return
    setFetchingBundle(key)
    try {
      if (url) {
        // Parse real ingredients from the recipe page
        const res = await fetch(`/api/parse-recipe?url=${encodeURIComponent(url)}`)
        const data = await res.json()
        if (!data.items?.length) throw new Error('No items parsed')
        setPendingBundle({
          name: data.recipeName || recipeName,
          emoji: '🛒',
          category: 'dinner',
          items: data.items,
          source_url: url,
        })
      } else {
        // Fall back to AI generation when no URL available
        const res = await fetch('/api/generate-bundle', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recipeName }),
        })
        const { bundle: bundleData } = await res.json()
        if (!bundleData) throw new Error('No bundle data returned')
        setPendingBundle(bundleData)
      }
    } catch (e) {
      console.error('buildBundle failed:', e)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I couldn't load that recipe — the site may block outside access. Try one of the other options, or paste the URL into a new bundle yourself.",
      }])
    }
    setFetchingBundle(null)
  }

  async function toggleBundle(bundle) {
    if (!householdId) return
    const isAdded = addedBundles.has(bundle.id)
    if (isAdded) {
      await supabase.from('bundles').update({ in_cart: false }).eq('id', bundle.id)
      await supabase.from('bundle_items').update({ in_cart: false, checked: false }).eq('bundle_id', bundle.id)
      setAddedBundles(prev => { const next = new Set(prev); next.delete(bundle.id); return next })
    } else {
      await supabase.from('bundles').update({ in_cart: true }).eq('id', bundle.id)
      await supabase.from('bundle_items').update({ in_cart: true, checked: false }).eq('bundle_id', bundle.id)
      setAddedBundles(prev => new Set([...prev, bundle.id]))
    }
  }

  async function sendMessage() {
    const text = input.trim()
    if (!text || loading) return

    const userMsg = { role: 'user', content: text }
    const nextMessages = [...messages, userMsg]
    setMessages(nextMessages)
    setInput('')
    setLoading(true)

    try {
      const res = await fetch('/api/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nextMessages, bundles, cartBundles, cartItems, planHistory }),
      })
      const data = await res.json()
      if (data.error) {
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${data.error}${data.details ? ' — ' + String(data.details).slice(0, 200) : ''}` }])
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.text || 'No response returned.' }])
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Check your connection and try again.' }])
    }

    setLoading(false)
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() }
  }

  const isEmpty = messages.length === 0 && !initializing

  if (pendingBundle) {
    return (
      <NewBundle
        user={user}
        customTabs={customTabs}
        initialName={pendingBundle.name}
        initialEmoji={pendingBundle.emoji}
        initialCategory={pendingBundle.category}
        initialItems={pendingBundle.items || []}
        initialSourceUrl={pendingBundle.source_url || ''}
        onBack={() => setPendingBundle(null)}
        onSaved={(savedBundle) => {
          setBundles(prev => [...prev, savedBundle])
          const key = pendingBundle.source_url || pendingBundle.name
          setBuiltBundles(prev => new Set([...prev, key]))
          setPendingBundle(null)
        }}
      />
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.navBar}>
        <button style={styles.backBtn} onClick={onBack}>← Back</button>
        <div style={styles.navTitle}>Assistant</div>
        <div style={{ width: 60 }} />
      </div>

      <div style={styles.chat}>
        {initializing ? (
          <div style={styles.centered}>
            <span style={styles.initText}>Getting your bundles…</span>
          </div>
        ) : isEmpty ? (
          <div style={styles.emptyState}>
            <div style={styles.butlerIcon}>🤵</div>
            <div style={styles.emptyTitle}>Your meal planning assistant</div>
            <div style={styles.emptySub}>Ask me to help plan your week, get fresh ideas, or mix things up from your usual rotation.</div>
            <div style={styles.promptChips}>
              {['Help me plan this week', 'I need new ideas', 'What should I add?'].map(p => (
                <button key={p} style={styles.promptChip} onClick={() => { setInput(p); inputRef.current?.focus() }}>{p}</button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <div key={i} style={msg.role === 'user' ? styles.userRow : styles.assistantRow}>
                {msg.role === 'assistant' && <div style={styles.avatarIcon}>🤵</div>}
                <div style={msg.role === 'user' ? styles.userBubble : styles.assistantBubble}>
                  {msg.role === 'assistant'
                    ? renderMessageContent(msg.content, bundles, addedBundles, toggleBundle, fetchingBundle, builtBundles, buildBundle)
                    : msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={styles.assistantRow}>
                <div style={styles.avatarIcon}>🤵</div>
                <div style={styles.assistantBubble}>
                  <span style={styles.typing}>●●●</span>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={styles.inputBar}>
        <textarea
          ref={inputRef}
          style={styles.input}
          placeholder="Ask me anything about your week…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <button
          style={{ ...styles.sendBtn, opacity: input.trim() && !loading ? 1 : 0.35 }}
          onClick={sendMessage}
          disabled={!input.trim() || loading}
        >
          ↑
        </button>
      </div>
    </div>
  )
}

const styles = {
  container: {
    maxWidth: '430px', margin: '0 auto', minHeight: '100vh',
    background: 'white', display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', sans-serif",
  },
  navBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px 10px', flexShrink: 0, borderBottom: '0.5px solid #eee',
  },
  backBtn: {
    fontSize: '13px', color: '#1D9E75', background: 'none',
    border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  navTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '20px', color: '#1a1a18' },
  chat: {
    flex: 1, overflowY: 'auto', padding: '16px 20px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  centered: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  initText: { fontSize: '13px', color: '#aaa' },
  emptyState: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '10px', paddingBottom: '40px',
  },
  butlerIcon: { fontSize: '48px', marginBottom: '4px' },
  emptyTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '22px', color: '#1a1a18' },
  emptySub: { fontSize: '13px', color: '#888', lineHeight: '1.6', maxWidth: '260px' },
  promptChips: { display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', maxWidth: '280px', marginTop: '8px' },
  promptChip: {
    padding: '10px 16px', background: '#f9f9f7', border: '0.5px solid #eee',
    borderRadius: '20px', fontSize: '13px', color: '#1a1a18', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  userRow: { display: 'flex', justifyContent: 'flex-end' },
  assistantRow: { display: 'flex', alignItems: 'flex-end', gap: '8px' },
  avatarIcon: { fontSize: '20px', flexShrink: 0, marginBottom: '2px' },
  userBubble: {
    background: '#1D9E75', color: 'white', borderRadius: '16px 16px 2px 16px',
    padding: '10px 14px', fontSize: '14px', lineHeight: '1.5',
    maxWidth: '78%', wordBreak: 'break-word',
  },
  assistantBubble: {
    background: '#f9f9f7', color: '#1a1a18', borderRadius: '2px 16px 16px 16px',
    padding: '10px 14px', fontSize: '14px', lineHeight: '1.6',
    maxWidth: '82%', wordBreak: 'break-word', border: '0.5px solid #eee',
    whiteSpace: 'pre-wrap',
  },
  typing: { color: '#aaa', letterSpacing: '2px', fontSize: '12px' },
  inputBar: {
    display: 'flex', alignItems: 'flex-end', gap: '10px',
    padding: '12px 20px 20px', borderTop: '0.5px solid #eee', flexShrink: 0,
  },
  input: {
    flex: 1, border: '0.5px solid #ddd', borderRadius: '14px',
    padding: '10px 14px', fontSize: '14px', fontFamily: "'DM Sans', sans-serif",
    color: '#1a1a18', resize: 'none', outline: 'none', background: '#f9f9f7',
    lineHeight: '1.5', maxHeight: '120px', overflowY: 'auto',
  },
  sendBtn: {
    width: '38px', height: '38px', borderRadius: '50%',
    background: '#1D9E75', border: 'none', color: 'white',
    fontSize: '18px', cursor: 'pointer', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
}
